import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Card,
  Tooltip,
  Modal,
  message,
  Badge,
  Descriptions,
  Steps,
  Divider,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  ExpandAltOutlined,
  UserOutlined,
  CalendarOutlined,
  StarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchDossiers,
  deleteDossier,
} from "../../../store/slices/dossierSlice";
import {
  fetchGroupeClients,
  fetchUsers,
} from "../../../store/slices/referenceSlice";
import { RootState, AppDispatch } from "../../../store";
import { Dossier, WorkflowAssignment } from "../../../types";
import { formatDate } from "../../../utils/dateUtils";
import {
  ListContainer,
  FilterCard,
  ActionButton,
  ExpandedCard,
  WorkflowSection,
} from "./DossierList.styles";
import {
  getPriorityColor,
  getPriorityText,
  getStatusText,
} from "../../../utils/priorityUtils";

const { Search } = Input;
const { Option } = Select;
const { Step } = Steps;
const { Text } = Typography;

const DossierList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { dossiers, loading, totalCount } = useSelector(
    (state: RootState) => state.dossiers
  );
  const { users, groupeClients } = useSelector(
    (state: RootState) => state.references
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [filters, setFilters] = useState({
    search: "",
    conservation: "",
    etat: [] as string[],
    gestionnaire: "",
    visiteur: "",
    elaborateur: "",
    validateur: "",
    groupe_client: "",
    titre_foncier: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const searchParams = {
      page: currentPage,
      ...(filters.search && { search: filters.search }),
      ...(filters.conservation && { conservation: filters.conservation }),
      ...(filters.etat.length > 0 && { etat: filters.etat.join(",") }),
      ...(filters.gestionnaire && {
        gestionnaire: parseInt(filters.gestionnaire),
      }),
      ...(filters.visiteur && {
        visiteur: parseInt(filters.visiteur),
      }),
      ...(filters.elaborateur && {
        elaborateur: parseInt(filters.elaborateur),
      }),
      ...(filters.validateur && {
        validateur: parseInt(filters.validateur),
      }),
      ...(filters.groupe_client && {
        groupe_client: parseInt(filters.groupe_client),
      }),
      ...(filters.titre_foncier && { titre_foncier: filters.titre_foncier }), // NOUVEAU
    };

    dispatch(fetchDossiers(searchParams));
    dispatch(fetchUsers());
  }, [dispatch, currentPage, filters]);

  useEffect(() => {
    dispatch(fetchGroupeClients());
  }, [dispatch]);

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Supprimer le dossier",
      content: "Êtes-vous sûr de vouloir supprimer ce dossier ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await dispatch(deleteDossier(id));
          message.success("Dossier supprimé avec succès");
          const searchParams = {
            page: currentPage,
            ...(filters.search && { search: filters.search }),
            ...(filters.conservation && { conservation: filters.conservation }),
            ...(filters.etat.length > 0 && { etat: filters.etat.join(",") }),
            ...(filters.gestionnaire && {
              gestionnaire: parseInt(filters.gestionnaire),
            }),
            ...(filters.visiteur && {
              visiteur: parseInt(filters.visiteur),
            }),
            ...(filters.elaborateur && {
              elaborateur: parseInt(filters.elaborateur),
            }),
            ...(filters.validateur && {
              validateur: parseInt(filters.validateur),
            }),
            ...(filters.groupe_client && {
              groupe_client: parseInt(filters.groupe_client),
            }),
            ...(filters.titre_foncier && {
              titre_foncier: filters.titre_foncier,
            }), // NOUVEAU
          };
          dispatch(fetchDossiers(searchParams));
        } catch (error) {
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };
  
  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      conservation: "",
      etat: [],
      gestionnaire: "",
      visiteur: "",
      elaborateur: "",
      validateur: "",
      groupe_client: "",
      titre_foncier: "",
    });
    setCurrentPage(1);
  };
  
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.conservation) count++;
    if (filters.etat.length > 0) count++;
    if (filters.gestionnaire) count++;
    if (filters.visiteur) count++;
    if (filters.elaborateur) count++;
    if (filters.validateur) count++;
    if (filters.groupe_client) count++;
    if (filters.titre_foncier) count++;
    return count;
  };

  const getEtatColor = (etat: string) => {
    const colors = {
      en_attente_visite: "blue",
      en_cours_visite: "processing",
      en_attente_elaboration: "orange",
      en_cours_elaboration: "processing",
      en_attente_validation: "gold",
      en_cours_validation: "processing",
      en_attente_modification: "red",
      en_cours_modification: "processing",
      modifie_att_validation: "purple", // NOUVEAU
      valide_att_honoraires: "purple",
      valide_a_envoyer: "green",
      envoye: "success",
    };
    return colors[etat as keyof typeof colors] || "default";
  };

  const getEtatText = (etat: string) => {
    const texts = {
      en_attente_visite: "En attente de visite",
      en_cours_visite: "En cours de visite",
      en_attente_elaboration: "En attente d'élaboration",
      en_cours_elaboration: "En cours d'élaboration",
      en_attente_validation: "En attente de validation",
      en_cours_validation: "En cours de validation",
      en_attente_modification: "En attente de modification",
      en_cours_modification: "En cours de modification",
      modifie_att_validation: "Modifié - Att. validation", // NOUVEAU
      valide_att_honoraires: "Validé - Att. honoraires",
      valide_a_envoyer: "Validé - À envoyer",
      envoye: "Envoyé",
    };
    return texts[etat as keyof typeof texts] || etat;
  };

  // Helper function to show which workflow phase we're in
  const getWorkflowPhase = (etat: string): string => {
    if (etat.includes("visite")) return "Phase Visite";
    if (etat.includes("elaboration")) return "Phase Élaboration";
    if (etat.includes("validation")) return "Phase Validation";
    if (etat.includes("modification")) return "Phase Modification";
    if (etat.startsWith("valide")) return "Phase Finale";
    if (etat === "envoye") return "Terminé";
    return "Phase Initiale";
  };

  // Get current step based on backend state
  const getCurrentStep = (etat: string) => {
    if (etat === "en_attente_visite") return 0;
    if (etat.includes("visite")) return 1;
    if (etat.includes("elaboration")) return 2;
    if (etat.includes("validation") || etat.includes("modification")) return 3;
    if (etat.startsWith("valide") || etat === "envoye") return 4;
    return 0;
  };

  // Get step status based on backend state
  const getStepStatus = (stepIndex: number, etat: string) => {
    const currentStep = getCurrentStep(etat);

    if (currentStep > stepIndex) return "finish";
    if (currentStep === stepIndex) {
      // Show 'process' for active states
      if (stepIndex === 1 && etat === "en_cours_visite") return "process";
      if (stepIndex === 2 && etat === "en_cours_elaboration") return "process";
      if (
        stepIndex === 3 &&
        (etat === "en_cours_validation" || etat === "en_cours_modification")
      )
        return "process";

      // Handle final step when dossier is sent
      if (stepIndex === 4 && etat === "envoye") return "finish";

      return "wait"; // For "en_attente" states
    }
    return "wait";
  };

  const getStateDescription = (etat: string): string => {
    const descriptions = {
      en_attente_visite: "Dossier créé, en attente d'assignation de visiteurs",
      en_cours_visite: "Visite terrain en cours",
      en_attente_elaboration:
        "Visite terminée, en attente d'assignation d'élaborateurs",
      en_cours_elaboration: "Élaboration du rapport en cours",
      en_attente_validation:
        "Élaboration terminée, en attente d'assignation de validateurs",
      en_cours_validation: "Validation du rapport en cours",
      en_attente_modification: "Modifications requises par le validateur",
      en_cours_modification: "Modifications en cours",
      valide_att_honoraires: "Rapport validé, en attente des honoraires",
      valide_a_envoyer: "Rapport validé, prêt à être envoyé",
      envoye: "Rapport envoyé au client",
    };
    return descriptions[etat as keyof typeof descriptions] || "État inconnu";
  };

  const getPhaseDescription = (
    phase: string,
    record: Dossier,
    etat: string
  ): string => {
    switch (phase) {
      case "visite":
        if (etat === "en_attente_visite") {
          return record.visiteurs && record.visiteurs.length > 0
            ? `${record.visiteurs.length} visiteur(s) assigné(s)`
            : "Aucun visiteur assigné";
        }
        if (etat === "en_cours_visite") {
          return record.visiteurs && record.visiteurs.length > 0
            ? `Visite en cours (${record.visiteurs.length} visiteur(s))`
            : "Visite en cours";
        }
        if (getCurrentStep(etat) > 1) {
          return record.visiteurs && record.visiteurs.length > 0
            ? `Visite terminée (${record.visiteurs.length} visiteur(s))`
            : "Visite terminée";
        }
        return "En attente";

      case "elaboration":
        if (etat === "en_attente_elaboration") {
          return record.elaborateurs && record.elaborateurs.length > 0
            ? `${record.elaborateurs.length} élaborateur(s) assigné(s)`
            : "Aucun élaborateur assigné";
        }
        if (etat === "en_cours_elaboration") {
          return record.elaborateurs && record.elaborateurs.length > 0
            ? `Élaboration en cours (${record.elaborateurs.length} élaborateur(s))`
            : "Élaboration en cours";
        }
        if (getCurrentStep(etat) > 2) {
          return record.elaborateurs && record.elaborateurs.length > 0
            ? `Élaboration terminée (${record.elaborateurs.length} élaborateur(s))`
            : "Élaboration terminée";
        }
        return "En attente";

      case "validation":
        if (etat === "en_attente_validation") {
          return record.validateurs && record.validateurs.length > 0
            ? `${record.validateurs.length} validateur(s) assigné(s)`
            : "Aucun validateur assigné";
        }
        if (etat.includes("validation") || etat.includes("modification")) {
          const status = etat.includes("modification")
            ? "Modification"
            : "Validation";
          return record.validateurs && record.validateurs.length > 0
            ? `${status} en cours (${record.validateurs.length} validateur(s))`
            : `${status} en cours`;
        }
        if (getCurrentStep(etat) > 3) {
          return record.validateurs && record.validateurs.length > 0
            ? `Validation terminée (${record.validateurs.length} validateur(s))`
            : "Validation terminée";
        }
        return "En attente";

      default:
        return "";
    }
  };

  // Expandable row render function
  const ExpandedRowRender = (record: Dossier) => {
    const etat = record.etat_dossier;

    return (
      <ExpandedCard>
        <Row gutter={24}>
          <Col span={12}>
            <h4>Informations générales</h4>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Groupe client">
                {record.groupe_client_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Typologie">
                {record.typologie_libelle}
              </Descriptions.Item>
              <Descriptions.Item label="Conservation">
                {record.conservation}
              </Descriptions.Item>
              <Descriptions.Item label="Localisation">
                {record.region_nom} → {record.province_nom} →{" "}
                {record.commune_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Département">
                {record.departement_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Date réception">
                {formatDate(record.Date_reception_demande)}
              </Descriptions.Item>
              <Descriptions.Item label="Date livraison prévue">
                {record.date_livraison_convenue
                  ? formatDate(record.date_livraison_convenue)
                  : "-"}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          <Col span={12}>
            <WorkflowSection>
              <h4>Progression du dossier</h4>

              {/* Current state display */}
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  backgroundColor: "#f6f6f6",
                  borderRadius: 6,
                }}
              >
                <Tag color={getEtatColor(etat)} style={{ marginRight: 8 }}>
                  {getEtatText(etat)}
                </Tag>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {getStateDescription(etat)}
                </Text>
              </div>

              {/* Simplified Steps - driven by backend state */}
              <Steps
                direction="vertical"
                size="small"
                current={getCurrentStep(etat)}
                style={{ marginBottom: 16 }}
              >
                <Step
                  title="Création"
                  description={`Gestionnaire: ${record.gestionnaire_nom}`}
                  status={getStepStatus(0, etat)}
                />
                <Step
                  title="Visite"
                  description={getPhaseDescription("visite", record, etat)}
                  status={getStepStatus(1, etat)}
                />
                <Step
                  title="Élaboration"
                  description={getPhaseDescription("elaboration", record, etat)}
                  status={getStepStatus(2, etat)}
                />
                <Step
                  title="Validation"
                  description={getPhaseDescription("validation", record, etat)}
                  status={getStepStatus(3, etat)}
                />
                <Step
                  title="Terminé"
                  description={
                    record.date_envoi_rapport
                      ? `Rapport envoyé le ${formatDate(
                          record.date_envoi_rapport
                        )}`
                      : etat.startsWith("valide")
                      ? "Validé, en attente d'envoi"
                      : "En attente"
                  }
                  status={getStepStatus(4, etat)}
                />
              </Steps>

              {/* Assignment details - informational only */}
              {((record.visiteurs?.length ?? 0) > 0 ||
                (record.elaborateurs?.length ?? 0) > 0 ||
                (record.validateurs?.length ?? 0) > 0) && (
                <div style={{ marginTop: 16 }}>
                  <Divider orientation="left" style={{ fontSize: "12px" }}>
                    Assignations
                  </Divider>

                  {record.visiteurs && record.visiteurs.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text
                        strong
                        style={{ fontSize: "12px", color: "#1890ff" }}
                      >
                        <EyeOutlined style={{ marginRight: 4 }} />
                        Visiteurs ({record.visiteurs.length}):
                      </Text>
                      <div
                        style={{
                          marginLeft: 16,
                          fontSize: "11px",
                          color: "#666",
                        }}
                      >
                        {record.visiteurs.map((v, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginBottom: 2,
                            }}
                          >
                            <span>• {v.user_name}</span>
                            {v.is_primary && (
                              <StarOutlined style={{ color: "#faad14" }} />
                            )}
                            {v.user_is_active === false && (
                              <Tag
                                color="orange"
                                style={{ fontSize: "9px", lineHeight: "14px" }}
                              >
                                Inactif
                              </Tag>
                            )}
                            {v.date_completed && (
                              <CheckCircleOutlined
                                style={{ color: "#52c41a" }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {record.elaborateurs && record.elaborateurs.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text
                        strong
                        style={{ fontSize: "12px", color: "#1890ff" }}
                      >
                        <EyeOutlined style={{ marginRight: 4 }} />
                        Elaborateurs ({record.elaborateurs.length}):
                      </Text>
                      <div
                        style={{
                          marginLeft: 16,
                          fontSize: "11px",
                          color: "#666",
                        }}
                      >
                        {record.elaborateurs.map((v, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginBottom: 2,
                            }}
                          >
                            <span>• {v.user_name}</span>
                            {v.is_primary && (
                              <StarOutlined style={{ color: "#faad14" }} />
                            )}
                            {v.user_is_active === false && (
                              <Tag
                                color="orange"
                                style={{ fontSize: "9px", lineHeight: "14px" }}
                              >
                                Inactif
                              </Tag>
                            )}
                            {v.date_completed && (
                              <CheckCircleOutlined
                                style={{ color: "#52c41a" }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {record.validateurs && record.validateurs.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text
                        strong
                        style={{ fontSize: "12px", color: "#52c41a" }}
                      >
                        <CheckCircleOutlined style={{ marginRight: 4 }} />
                        Validateurs ({record.validateurs.length}):
                      </Text>
                      <div
                        style={{
                          marginLeft: 16,
                          fontSize: "11px",
                          color: "#666",
                        }}
                      >
                        {record.validateurs.map((v, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginBottom: 2,
                            }}
                          >
                            <span>• {v.user_name}</span>
                            {v.is_primary && (
                              <StarOutlined style={{ color: "#faad14" }} />
                            )}
                            {v.user_is_active === false && (
                              <Tag
                                color="orange"
                                style={{ fontSize: "9px", lineHeight: "14px" }}
                              >
                                Inactif
                              </Tag>
                            )}
                            {v.date_prevalidation && (
                              <ClockCircleOutlined
                                style={{ color: "#faad14" }}
                              />
                            )}
                            {v.date_completed && (
                              <CheckCircleOutlined
                                style={{ color: "#52c41a" }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </WorkflowSection>
          </Col>
        </Row>
      </ExpandedCard>
    );
  };

  // Main table columns
  const columns = [
    {
      title: "Numéro",
      dataIndex: "numero_dossier",
      key: "numero_dossier",
      width: 120,
      fixed: "left" as const,
      render: (text: string, record: Dossier) => (
        <div>
          <Button
            type="link"
            onClick={() => navigate(`/dossiers/${record.id}`)}
            style={{ padding: 0, fontWeight: 500, fontSize: "13px" }}
          >
            {text}
          </Button>
          <div style={{ fontSize: "11px", color: "#999" }}>
            {formatDate(record.Date_reception_demande)}
          </div>
        </div>
      ),
    },
    {
      title: "Client & Détails",
      key: "client_details",
      width: 300,
      render: (record: Dossier) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>
            {record.client}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.groupe_client_nom} • {record.typologie_libelle}
          </div>
          <div style={{ fontSize: "11px", color: "#999", marginTop: 2 }}>
            {record.region_nom} - {record.conservation}
          </div>
        </div>
      ),
    },
    {
      title: "État du dossier",
      dataIndex: "etat_dossier",
      key: "etat_dossier",
      width: 150,
      render: (etat: string) => (
        <div style={{ textAlign: "center" }}>
          <Tag
            color={getEtatColor(etat)}
            style={{ fontSize: "11px", marginBottom: 4 }}
          >
            {getEtatText(etat)}
          </Tag>
          <div style={{ fontSize: "10px", color: "#999" }}>
            {getWorkflowPhase(etat)}
          </div>
        </div>
      ),
    },
    {
      title: "Priorité",
      dataIndex: "priorite",
      key: "priorite",
      width: 100,
      fixed: "right" as const,
      render: (priority: number) => (
        <Tag color={getPriorityColor(priority)} style={{ fontSize: "11px" }}>
          {getPriorityText(priority)}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: Dossier) => (
        <Space size="small">
          <Tooltip title="Voir">
            <ActionButton
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dossiers/${record.id}`)}
            />
          </Tooltip>
          {user?.is_gestionnaire && (
            <>
              <Tooltip title="Modifier">
                <ActionButton
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/dossiers/${record.id}/edit`)}
                />
              </Tooltip>
              <Tooltip title="Supprimer">
                <ActionButton
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const conservationOptions = [
    "AGADIR",
    "ELHAOUZ",
    "AL HOCEIMA",
    "AZEMMOUR",
    "AZILAL",
    "BEN GUERIR",
    "BENI MELLAL",
    "BENSLIMANE",
    "BERKANE",
    "BERRECHID",
    "BOULMANE",
    "MISSOUR",
    "CASA AIN CHOCK",
    "CASA AIN SBAA HAY MOHAMMADI",
    "CASA ANFA",
    "CASA ALFIDA MERS SULTAN",
    "CASA HAY HASSANI",
    "CASA MAARIF",
    "CASA NOUACEUR",
    "CASA SIDI EL BERNOUSSI - ZENATA",
    "CASA SIDI OTHMANE",
    "CHICHAOUA",
    "CHTOUKA AIT BAHA",
    "EL HAJEB",
    "EL JADIDA",
    "EL KELAA DES SRAGHNA",
    "ERRACHIDIA",
    "ESSAOUIRA",
    "FES",
    "FES ZOUAGHA",
    "FQUIH BEN SALEH",
    "GUELMIM",
    "GUERCIF",
    "HAD SOUALEM",
    "HARHOURA-SKHIRAT",
    "IFRANE",
    "INZEGANE AIT MELLOUL",
    "KARIAT BA MOHAMED",
    "KENITRA",
    "KHMISSET",
    "KHNIFRA",
    "KHOURIBGA",
    "LAAYOUNE",
    "LARACHE",
    "MARRAKECH GUELIZ",
    "MARRAKECH MENARA",
    "MARRAKECH SIDI YOUSSEF BEN ALI",
    "M'DIQ FNIDEQ",
    "MEDYOUNA",
    "MEKNES AL ISMAILIA",
    "MEKNES EL MENZEH",
    "MIDELT",
    "MOHAMMADIA",
    "NADOR",
    "OUARZAZATE",
    "OUJDA",
    "OUJDA ANGAD",
    "RABAT",
    "RABAT RYAD",
    "ROMMANI",
    "SAFI",
    "SALE",
    "SALE EL JADIDA",
    "SEFROU",
    "SETTAT",
    "SIDI BENNOUR",
    "SIDI KACEM",
    "SIDI SLIMANE",
    "SIDI SMAIL",
    "ZEMAMRA",
    "SOUK LARBAA",
    "SOUK SEBT OULED NEMMA",
    "TAMESNA",
    "TANGER BENI MAKADA",
    "TANGER VILLE",
    "TAOUNAT",
    "TAOURIRT",
    "TAROUDANT",
    "TAZA",
    "TEMARA",
    "TETOUAN",
    "TIFELT",
    "TIZNIT",
  ];

  // Helper functions to get users by role
  const getUsersByRole = (role: string) => {
    return users.filter((u) => u.role === role && u.is_active);
  };

  return (
    <ListContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h1>Gestion des dossiers</h1>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Filtres actifs et bouton effacer - déplacés ici */}
          {getActiveFiltersCount() > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Badge count={getActiveFiltersCount()} size="small">
                <Text type="secondary">Filtres actifs</Text>
              </Badge>
              <Button
                type="text"
                size="small"
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
              >
                Tout effacer
              </Button>
            </div>
          )}

          {user?.is_gestionnaire && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/dossiers/new")}
              size="large"
            >
              Nouveau dossier
            </Button>
          )}
        </div>
      </div>

      <FilterCard>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Rechercher par client..."
              allowClear
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Rechercher par titre foncier..." // NOUVEAU
              allowClear
              value={filters.titre_foncier}
              onChange={(e) =>
                handleFilterChange("titre_foncier", e.target.value)
              }
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Groupe client"
              allowClear
              style={{ width: "100%" }}
              value={filters.groupe_client || undefined}
              onChange={(value) =>
                handleFilterChange("groupe_client", value || "")
              }
              showSearch
              optionFilterProp="children"
            >
              {groupeClients.map((gc) => (
                <Option key={gc.id} value={gc.id}>
                  {gc.nom}
                </Option>
              ))}
            </Select>
          </Col>
          {/* <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Conservation"
              allowClear
              style={{ width: "100%" }}
              value={filters.conservation || undefined}
              onChange={(value) =>
                handleFilterChange("conservation", value || "")
              }
            >
              {conservationOptions.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Col> */}
{/* test */}
          
          <Col xs={24} sm={12} md={6}>
            <Select
              mode="multiple"
              placeholder="États"
              allowClear
              style={{ width: "100%" }}
              value={filters.etat}
              onChange={(value) => handleFilterChange("etat", value || [])}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+${omittedValues.length} autres`
              }
            >
              <Option value="en_attente_visite">En attente de visite</Option>
              <Option value="en_cours_visite">En cours de visite</Option>
              <Option value="en_attente_elaboration">
                En attente d'élaboration
              </Option>
              <Option value="en_cours_elaboration">
                En cours d'élaboration
              </Option>
              <Option value="en_attente_validation">
                En attente de validation
              </Option>
              <Option value="en_cours_validation">
                En cours de validation
              </Option>
              <Option value="en_attente_modification">
                En attente de modification
              </Option>
              <Option value="en_cours_modification">
                En cours de modification
              </Option>
              <Option value="modifie_att_validation">
                Modifié - En attente de validation
              </Option>{" "}
              {/* NOUVEAU */}
              <Option value="valide_att_honoraires">
                Validé - En attente des honoraires
              </Option>
              <Option value="valide_a_envoyer">Validé - À envoyer</Option>
              <Option value="envoye">Envoyé</Option>
            </Select>
          </Col>
        </Row>

        {/* Second row for additional filters - TOUS LES FILTRES MAINTENANT DISPONIBLES POUR TOUS */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* MODIFICATION : Suppression de la condition user?.is_gestionnaire */}
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Gestionnaire"
              allowClear
              style={{ width: "100%" }}
              value={filters.gestionnaire || undefined}
              onChange={(value) =>
                handleFilterChange("gestionnaire", value || "")
              }
              showSearch
              optionFilterProp="children"
            >
              {getUsersByRole("gestionnaire").map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Visiteur"
              allowClear
              style={{ width: "100%" }}
              value={filters.visiteur || undefined}
              onChange={(value) => handleFilterChange("visiteur", value || "")}
              showSearch
              optionFilterProp="children"
            >
              {getUsersByRole("visiteur").map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Élaborateur"
              allowClear
              style={{ width: "100%" }}
              value={filters.elaborateur || undefined}
              onChange={(value) =>
                handleFilterChange("elaborateur", value || "")
              }
              showSearch
              optionFilterProp="children"
            >
              {getUsersByRole("elaborateur").map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Validateur"
              allowClear
              style={{ width: "100%" }}
              value={filters.validateur || undefined}
              onChange={(value) =>
                handleFilterChange("validateur", value || "")
              }
              showSearch
              optionFilterProp="children"
            >
              {getUsersByRole("validateur").map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </FilterCard>

      <Card>
        <Table
          columns={columns}
          dataSource={dossiers}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender: ExpandedRowRender,
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <Button
                  type="text"
                  size="small"
                  icon={
                    <ExpandAltOutlined
                      style={{ transform: "rotate(180deg)" }}
                    />
                  }
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <Button
                  type="text"
                  size="small"
                  icon={<ExpandAltOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              ),
            rowExpandable: () => true,
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `Total: ${total} dossiers`,
            onChange: setCurrentPage,
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>
    </ListContainer>
  );
};

export default DossierList;